import MarketingImage from '@/components/landing/MarketingImage';
import React from 'react';
import SignupLink from '@/components/SignupLink';
const gdprReady = '/images/marketing/landing-gdpr-badges-gdpr-ready.webp';
const ukEuGdprPrivacyReady = '/images/marketing/landing-gdpr-badges-uk-eu-gdpr-privacy-ready.webp';
const gdprCompliant = '/images/marketing/landing-gdpr-badges-gdpr-compliant.webp';
const HeroSectionImage = '/images/marketing/hero-section.webp';
import { scrollToLandingLeadForm } from '@/utils/landingLeadIntent';
import styles from './HeroSection.module.css';

type HeroSectionProps = {
  /** /start keeps its tracking IDs and immediate paint while sharing the homepage hero. */
  variant?: 'default' | 'start';
};

// LANDING PAGE COMPONENT: HeroSection
// This component can be easily separated for launching the landing page first
const HeroSection = ({ variant = 'default' }: HeroSectionProps) => {
  const isStart = variant === 'start';

  return (
    <section
      id={isStart ? 'start-hero' : undefined}
      className={styles.heroSection}
    >
      <div className={styles.heroCanvas}>
        <div className={styles.heroContent}>
          <div className={styles.leftPane}>
            <div className={`${styles.complianceRow} ${isStart ? '' : 'animate-fade-in'}`}>
              <MarketingImage
                loading="eager"
                src={gdprReady}
                srcSet={`/images/marketing/landing-gdpr-badges-gdpr-ready-320.webp 320w, ${gdprReady} 640w`}
                sizes="(max-width: 40rem) 26vw, 9rem"
                alt="GDPR ready"
                width={712}
                height={278}
              />
              <MarketingImage
                loading="eager"
                src={ukEuGdprPrivacyReady}
                srcSet={`/images/marketing/landing-gdpr-badges-uk-eu-gdpr-privacy-ready-480.webp 480w, ${ukEuGdprPrivacyReady} 640w`}
                sizes="(max-width: 40rem) 40vw, 13rem"
                alt="UK and EU GDPR privacy ready"
                width={1084}
                height={278}
              />
              <MarketingImage
                loading="eager"
                src={gdprCompliant}
                srcSet={`/images/marketing/landing-gdpr-badges-gdpr-compliant-320.webp 320w, ${gdprCompliant} 640w`}
                sizes="(max-width: 40rem) 25vw, 9rem"
                alt="GDPR compliant"
                width={686}
                height={278}
              />
            </div>

            <h1 className={`${styles.heroTitle} ${isStart ? '' : 'animate-fade-in'}`}>
              <span className={styles.heroTitleLine}>
                The intelligence layer powering the future of{' '}
                <span className={styles.heroTitleAccent}>personalized nutrition.</span>
              </span>
            </h1>

            <p
              className={`${styles.heroLead} ${isStart ? '' : 'animate-fade-in'}`}
              style={isStart ? undefined : { animationDelay: '0.1s' }}
            >
              Scale your nutrition practice <span className={styles.gradientText}>3X</span> — without scaling your workload.
            </p>

            {/* <p
              className={`${styles.heroDescription} animate-fade-in`}
              style={{ animationDelay: '0.2s' }}
            >
              CalorieScience helps practitioners transform complex client data, biomarkers, and nutrition science into personalized care — reducing admin, improving efficiency, and enabling practice growth.
            </p> */}

            <div
              className={`${styles.actionRow} ${isStart ? '' : 'animate-fade-in'}`}
              style={isStart ? undefined : { animationDelay: '0.3s' }}
            >
              {/* <Button
                component="a"
                href="#ready-to-grow-practice"
                className={styles.exploreButton}
                data-analytics-event="Landing CTA Clicked"
                data-analytics-id="hero_join_beta"
                data-analytics-label="Join our Beta Program"
                data-analytics-section="hero"
              >
                Join our Beta Program
              </Button> */}
              <SignupLink
                className={styles.getStartedButton}
                data-analytics-event="Landing CTA Clicked"
                data-analytics-id={isStart ? 'start_hero_get_started' : 'hero_get_started'}
                data-analytics-label={isStart ? 'Get Started' : 'Start for Free'}
                data-analytics-section="hero"
              >
                {isStart ? 'Get Started' : 'Start for Free'}
              </SignupLink>
              {!isStart && (
                <button
                  type="button"
                  className={styles.bookDemoButton}
                  onClick={() => scrollToLandingLeadForm('book_demo')}
                  data-analytics-event="Landing CTA Clicked"
                  data-analytics-id="hero_book_demo"
                  data-analytics-label="Book a Demo"
                  data-analytics-section="hero"
                >
                  Book a Demo
                </button>
              )}
            </div>
            {isStart && <p className={styles.trialNote}>31 days free. No credit card.</p>}
          </div>

          <div className={styles.rightPane}>
            <MarketingImage loading="eager"
              fetchPriority="high"
              sizes="(max-width: 21.875rem) calc(100vw - 1.75rem), (max-width: 48rem) min(92vw, 32rem), (max-width: 64rem) min(92vw, 42rem), (max-width: 80rem) calc(46vw - 0.875rem), min(calc(45.08vw - 1.47rem), 43.375rem)"
              srcSet={`/images/marketing/hero-section-400.webp 400w, /images/marketing/hero-section-600.webp 600w, /images/marketing/hero-section-800.webp 800w, /images/marketing/hero-section-900.webp 900w, ${HeroSectionImage} 1200w`}
              src={HeroSectionImage}
              width={1200}
              height={949}
              alt="CalorieScience platform preview"
              className={styles.heroImage}
            />
          </div>
        </div>
      </div>

      {/* <div className={styles.professionStrip}> */}
        {/* <div className={styles.professionTrack}>
          <span>Clinical Nutritionists</span>
          <span className={styles.dot}>◉</span>
          <span>Dietitians</span>
          <span className={styles.dot}>◉</span>
          <span>Fitness and Health Coaches</span>
          <span className={styles.dot}>◉</span>
          <span>Mental Health Professionals</span>
          <span className={styles.dot}>◉</span>
          <span>Clinical Nutritionists</span>
          <span className={styles.dot}>◉</span>
          <span>Dietitians</span>
          <span className={styles.dot}>◉</span>
          <span>Fitness and Health Coaches</span>
          <span className={styles.dot}>◉</span>
          <span>Mental Health Professionals</span>
        </div> */}
      {/* </div> */}
    </section>
  );
};

export default HeroSection;
